import { useEffect, useId, useRef } from 'react';
import { ChevronDown, X } from 'lucide-react';
import styles from './FilterBar.module.css';

export default function FilterBar({
  filters,
  noun = 'records',
  actions,
}) {
  const rootRef = useRef(null);
  const menuGroup = useId();

  const {
    definitions,
    query,
    values,
    options,
    filtered,
    total,
    setQuery,
    toggle,
    clear,
  } = filters;

  const active = definitions.flatMap(field =>
    values[field.key].map(value => ({
      key: field.key,
      label: field.label,
      value,
    }))
  );

  useEffect(() => {
    function handlePointerDown(event) {
      rootRef.current
        ?.querySelectorAll('details[open]')
        .forEach(menu => {
          if (!menu.contains(event.target)) {
            menu.open = false;
          }
        });
    }

    document.addEventListener('pointerdown', handlePointerDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, []);

  return (
    <div ref={rootRef} className={styles.filterBar}>
      <div className={styles.controls}>
        <label className={styles.search}>
          <span className={styles.srOnly}>Search {noun}</span>

          <input
            type="search"
            value={query}
            placeholder={`Search ${noun}…`}
            onChange={event => setQuery(event.target.value)}
          />
        </label>

        {definitions.map(field => (
          <details
            key={field.key}
            name={menuGroup}
            className={styles.menu}
            onKeyDown={event => {
              if (event.key !== 'Escape') return;

              event.preventDefault();
              event.stopPropagation();
              event.currentTarget.open = false;
              event.currentTarget.querySelector('summary')?.focus();
            }}
          >
            <summary>
              {field.label}

              {values[field.key].length > 0 && (
                <span className={styles.count}>
                  {values[field.key].length}
                </span>
              )}

              <ChevronDown size={14} aria-hidden="true" />
            </summary>

            <fieldset className={styles.options}>
              <legend className={styles.srOnly}>
                {field.label}
              </legend>

              {options[field.key].map(value => (
                <label key={value} className={styles.option}>
                  <input
                    type="checkbox"
                    checked={values[field.key].includes(value)}
                    onChange={() => toggle(field.key, value)}
                  />
                  <span>{value}</span>
                </label>
              ))}

              {options[field.key].length === 0 && (
                <p>No values in this scope.</p>
              )}
            </fieldset>
          </details>
        ))}

        {actions && (
          <div className={styles.actions}>{actions}</div>
        )}
      </div>

      <div className={styles.summary}>
        <strong role="status">
          {filtered.length} of {total} {noun}
        </strong>

        <span>
          Match any selection within a filter; match all filters together.
        </span>
      </div>

      {(active.length > 0 || query) && (
        <div className={styles.chips}>
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Remove search"
            >
              Search: {query}
              <X size={12} aria-hidden="true" />
            </button>
          )}

          {active.map(filter => (
            <button
              key={JSON.stringify([filter.key, filter.value])}
              type="button"
              onClick={() => toggle(filter.key, filter.value)}
              aria-label={`Remove ${filter.label}: ${filter.value}`}
            >
              {filter.label}: {filter.value}
              <X size={12} aria-hidden="true" />
            </button>
          ))}

          <button
            type="button"
            className={styles.clear}
            onClick={clear}
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
}